import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ColumnDef } from '@tanstack/react-table';
import { Box, IconButton, Skeleton, Switch, Typography, useTheme } from '@mui/material';
import { MdOutlineLocalPhone, MdOutlineRemoveRedEye } from 'react-icons/md';
import { GoPlus } from 'react-icons/go';
import Search from 'components/Common/Search';
import Select from 'components/Common/Select';
import Button from 'components/Common/Button';
import DataTable from 'components/Common/DataTable';
import Pagination, { usePagination } from 'components/Common/Pagination';
import CallLogForm from 'pages/Business/Agent/CallLogs/components/CallLogForm';
import AssociatedRecords from './components/AssociatedRecords';
import AddContactDrawer from './components/AddContactDrawer';
import { useGetContacts, useUpdateContactMutation } from 'apis/business/agent/contact/hook';
import {
  AssociatedRecord,
  CONTACT_CATEGORY_VALUES,
  ContactCategory,
  ContactResponse
} from 'apis/business/agent/contact/types';

interface FilterFormData {
  category: string;
}

const defaultFormValues: FilterFormData = {
  category: 'all'
};

function associatedRecordNames(records: AssociatedRecord[] | undefined): string[] {
  return (records ?? []).map((record) => {
    const name = [record.first_name, record.last_name].filter(Boolean).join(' ').trim();
    return name || 'Unknown Caller';
  });
}

export default function Contacts() {
  const theme = useTheme();
  const { control, watch } = useForm<FilterFormData>({
    defaultValues: defaultFormValues
  });
  const { currentPage, perPage, setPage, setPerPage, resetPage } = usePagination();

  const [searchText, setSearchText] = useState<string | null>(null);
  const [filterSearchText, setFilterSearchText] = useState<string | null>(null);
  const [callLogId, setCallLogId] = useState<string>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [updatingCategoryId, setUpdatingCategoryId] = useState<number | null>(null);

  const selectedCategory = watch('category');

  useEffect(() => {
    setFilterSearchText(searchText);
    resetPage();
  }, [searchText, resetPage]);

  useEffect(() => {
    resetPage();
  }, [selectedCategory, resetPage]);

  const categoryFilterValue =
    selectedCategory !== 'all' ? CONTACT_CATEGORY_VALUES[selectedCategory as ContactCategory] : undefined;

  const {
    data: contactsResponse,
    isLoading,
    isFetching
  } = useGetContacts({
    ...(categoryFilterValue !== undefined && { 'filter[category_eq]': categoryFilterValue }),
    'filter[search_number_or_name]': filterSearchText || undefined,
    page: currentPage,
    per_page: perPage
  });

  const { mutate: updateContact, isPending } = useUpdateContactMutation();

  useEffect(() => {
    if (updatingCategoryId != null && !isPending && !isFetching) {
      setUpdatingCategoryId(null);
    }
  }, [updatingCategoryId, isPending, isFetching]);

  const handleVipChange = (id: number, isVip: boolean) => {
    setUpdatingCategoryId(id);
    updateContact({ id, payload: { contact: { category: isVip ? 'vip' : 'general' } } });
  };

  const handleOpenInteraction = (interactionId: number | null) => {
    if (!interactionId) return;
    setCallLogId(String(interactionId));
    setDrawerOpen(true);
  };

  return (
    <>
      <Box
        display='flex'
        flexDirection={{ xs: 'column', lg: 'row' }}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        mb={{ xs: 2, lg: 3 }}
        gap={{ xs: 2, lg: 0 }}
      >
        <Typography variant='h6' fontWeight={500}>
          Contacts
        </Typography>
        <Box display='flex' flexDirection={{ xs: 'column', sm: 'row' }} gap={1} width={{ xs: '100%', lg: 'auto' }}>
          <Box width={{ xs: '100%', lg: '280px' }}>
            <Search
              size='small'
              searchText={searchText}
              setSearchText={setSearchText}
              placeholder='Search number or name'
              fullWidth
            />
          </Box>
          <Box width={{ xs: '100%', lg: '170px' }}>
            <Select
              name='category'
              label='Categories'
              control={control}
              sortOrder='none'
              options={[
                { value: 'all', label: 'All' },
                { value: 'vip', label: 'VIP' }
              ]}
              sx={{ height: '40px', background: theme.palette.background.paper }}
            />
          </Box>
          <Button
            variant='contained'
            color='primary'
            startIcon={<GoPlus />}
            onClick={() => setAddContactOpen(true)}
            sx={{ height: 40 }}
          >
            Add Contact
          </Button>
        </Box>
      </Box>

      <DataTable
        data={contactsResponse?.data}
        columns={columns(handleVipChange, handleOpenInteraction, updatingCategoryId)}
        isHeaderSticky
        isLoading={isLoading}
      />
      <Pagination
        totalPages={contactsResponse?.meta?.total_pages}
        totalCount={contactsResponse?.meta?.total_count ?? 0}
        currentPage={currentPage}
        perPage={perPage}
        onChange={setPage}
        onPerPageChange={setPerPage}
      />

      <CallLogForm
        key={`${callLogId}`}
        logId={callLogId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        showInteractionTypeFilter
        businessScoped
      />

      <AddContactDrawer open={addContactOpen} onClose={() => setAddContactOpen(false)} />
    </>
  );
}

const columns = (
  onVipChange: (id: number, isVip: boolean) => void,
  onOpenInteraction: (interactionId: number | null) => void,
  updatingCategoryId: number | null
): ColumnDef<ContactResponse>[] => [
  {
    header: 'NUMBER',
    cell: (info) => (
      <Box display='flex' alignItems='center' gap={1}>
        <MdOutlineLocalPhone size={18} color='#6B7280' />
        <Typography variant='body2'>{info.row.original.phone_number || '-'}</Typography>
      </Box>
    )
  },
  {
    header: 'ASSOCIATED RECORDS',
    cell: (info) => <AssociatedRecords records={associatedRecordNames(info.row.original.associated_records)} />
  },
  {
    header: 'IS VIP',
    cell: (info) =>
      info.row.original.id === updatingCategoryId ? (
        <Skeleton variant='rectangular' width={40} height={24} sx={{ borderRadius: '12px' }} />
      ) : (
        <Switch
          size='small'
          checked={info.row.original.category === 'vip'}
          onChange={(_, isVip) => onVipChange(info.row.original.id, isVip)}
          inputProps={{ 'aria-label': 'VIP status' }}
        />
      )
  },
  {
    header: 'CHAT',
    cell: (info) => <Typography>{info.row.original.chats_count ?? 0}</Typography>
  },
  {
    header: 'CALLS',
    cell: (info) => <Typography>{info.row.original.calls_count ?? 0}</Typography>
  },
  {
    header: 'ACTION',
    cell: (info) => {
      const { latest_interaction_id } = info.row.original;

      return (
        <IconButton
          size='small'
          aria-label='View interaction'
          disabled={!latest_interaction_id}
          onClick={() => onOpenInteraction(latest_interaction_id)}
        >
          <MdOutlineRemoveRedEye size={24} />
        </IconButton>
      );
    }
  }
];
